import React from "react";
import styled from "@emotion/styled";
import { Typography } from "../Typography";

type ColumnContent = "numeric" | "text" | "node";

type ColumnTextProps = {
  columnContentType?: ColumnContent;
  hasTotal?: boolean;
};

const Table = styled.table`
  position: relative;
  overflow-x: auto;
  border-spacing: 0px;
  border-radius: 5px;
  width: 100%;
`;

const ColumnText = styled(Typography)<ColumnTextProps>`
  box-sizing: border-box;
  line-height: normal;
  padding: 0px 0px 8px 24px;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out;
  text-align: ${({ columnContentType }) =>
    columnContentType === "numeric" ? "right" : "left"};

  &:first-of-type {
    text-align: left;
    padding-left: 24px;
  }

  &:last-of-type {
    padding-right: 18px;
  }
`;

const DataCell = styled(ColumnText)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.mist};
  max-width: 300px;
  white-space: initial !important;
  vertical-align: baseline;
  padding: 10px 0px 9px 18px;
`;

const Heading = styled(Typography)`
  margin-bottom: 36px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const ContentTableRow = styled.tr`
  &:nth-child(odd) {
    background-color: ${({ theme }) => theme.colors.mist};
  }

  &:nth-child(even) {
    background-color: ${({ theme }) =>
      theme.colors.opacity(theme.colors.mist, 0.2)};
  }
`;

export type DataTableProps = {
  headings: string[] | undefined;
  rows: Array<Array<string | number | React.ReactNode>>;
  columnContentTypes: ColumnContent[];
  title?: string;
  className?: string;
};

export const DataTable = ({
  headings,
  title,
  rows,
  columnContentTypes,
  className,
}: DataTableProps) => {
  return (
    <div className={className}>
      {title && (
        <Heading use="componentTitle" as="h2">
          {title}
        </Heading>
      )}
      <TableWrapper>
        <Table>
          {headings && headings.length > 0 && (
            <thead>
              <tr>
                {headings.map((heading, i) => (
                  <ColumnText
                    as="th"
                    columnContentType={columnContentTypes[i]}
                    key={`title-${i}`}
                    use="body2"
                  >
                    {heading}
                  </ColumnText>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, i) => (
              <ContentTableRow key={`row-${i}`}>
                {row.map((cellContent, i) => (
                  <DataCell
                    as="td"
                    columnContentType={columnContentTypes[i]}
                    key={`cell-${i}`}
                    use="body1"
                  >
                    {cellContent}
                  </DataCell>
                ))}
              </ContentTableRow>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
};
